/**
 * Enhanced DataTable Component
 *
 * Standardized reusable data table with:
 * - Column definitions with sorting
 * - Built-in search functionality
 * - Pagination support
 * - Loading states with skeletons
 * - Empty states with custom messages
 * - Row click handlers
 * - Consistent styling
 */

import { ReactNode } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ArrowUp, ArrowDown, Search } from 'lucide-react'

export interface ColumnDef<T = any> {
  key: string
  header: string | ReactNode
  cell?: (row: T, index: number) => ReactNode
  className?: string
  headerClassName?: string
  sortable?: boolean
  accessor?: (row: T) => any
}

export interface DataTableProps<T = any> {
  columns: ColumnDef<T>[]
  data: T[]
  isLoading?: boolean
  emptyMessage?: string
  emptyIcon?: ReactNode
  className?: string

  // Search props
  searchable?: boolean
  searchValue?: string
  searchPlaceholder?: string
  onSearchChange?: (value: string) => void

  // Sorting props
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  onSortChange?: (field: string, order: 'asc' | 'desc') => void

  // Pagination props
  pagination?: {
    currentPage: number
    totalItems: number
    pageSize: number
    onPageChange: (page: number) => void
  }

  // Optional filters slot
  filters?: ReactNode

  // Row props
  onRowClick?: (row: T) => void
  rowClassName?: string | ((row: T) => string)
}

export function DataTable<T = any>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
  emptyIcon,
  className,
  searchable = false,
  searchValue = '',
  searchPlaceholder = 'Search...',
  onSearchChange,
  sortField,
  sortOrder,
  onSortChange,
  pagination,
  filters,
  onRowClick,
  rowClassName,
}: DataTableProps<T>) {
  // Handle column sort click
  const handleSort = (column: ColumnDef<T>) => {
    if (!column.sortable || !onSortChange) return

    const newOrder = sortField === column.key && sortOrder === 'asc' ? 'desc' : 'asc'
    onSortChange(column.key, newOrder)
  }

  // Render sort icon
  const renderSortIcon = (column: ColumnDef<T>) => {
    if (!column.sortable) return null

    const isActive = sortField === column.key

    return (
      <span className="ml-2 inline-flex">
        {isActive && sortOrder === 'asc' && <ArrowUp className="h-4 w-4" />}
        {isActive && sortOrder === 'desc' && <ArrowDown className="h-4 w-4" />}
        {!isActive && <ArrowUp className="h-4 w-4 opacity-30" />}
      </span>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        {/* Search bar skeleton */}
        {searchable && (
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 flex-1" />
          </div>
        )}

        {/* Filters skeleton */}
        {filters && <div className="mb-4">{filters}</div>}

        {/* Table skeleton */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.key} className={column.headerClassName}>
                    {typeof column.header === 'string' ? column.header : column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination skeleton */}
        {pagination && <Skeleton className="h-10 w-full" />}
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={cn('space-y-4', className)}>
        {/* Search bar */}
        {searchable && (
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        )}

        {/* Filters */}
        {filters && <div className="mb-4">{filters}</div>}

        {/* Empty state */}
        <div className="rounded-md border">
          <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
            {emptyIcon && <div className="mb-4">{emptyIcon}</div>}
            <p>{emptyMessage}</p>
          </div>
        </div>
      </div>
    )
  }

  // Helper to get cell content
  const getCellContent = (row: T, column: ColumnDef<T>, index: number) => {
    if (column.cell) {
      return column.cell(row, index)
    }
    // Use accessor if provided, otherwise access property by key
    const value = column.accessor ? column.accessor(row) : (row as any)[column.key]
    // Return fallback for null/undefined
    return value !== null && value !== undefined ? value : '—'
  }

  // Helper to get row class
  const getRowClass = (row: T) => {
    if (typeof rowClassName === 'function') {
      return rowClassName(row)
    }
    return rowClassName
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search bar */}
      {searchable && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      )}

      {/* Filters slot */}
      {filters && <div>{filters}</div>}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    column.headerClassName,
                    column.sortable && onSortChange && 'cursor-pointer select-none hover:bg-muted/50'
                  )}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center">
                    {typeof column.header === 'string' ? column.header : column.header}
                    {renderSortIcon(column)}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={index}
                onClick={() => onRowClick?.(row)}
                className={cn(
                  onRowClick && 'cursor-pointer hover:bg-muted/50',
                  getRowClass(row)
                )}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} className={column.className}>
                    {getCellContent(row, column, index)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
        />
      )}
    </div>
  )
}
